<template>
  <div class="card">
    <div class="card-header bg-info text-white">
      <h5 class="mb-0">报站模板配置</h5>
    </div>
    <div class="card-body">
      <!-- 模板内容配置 -->
      <div class="template-container">
        <!-- 报站类型按钮导航：第一行（固定四类） -->
        <div class="station-type-btns mb-2">
          <button
            v-for="tab in mainTabs"
            :key="tab.value"
            type="button"
            class="btn btn-sm btn-outline-primary station-type-btn"
            :class="{ active: activeTab === tab.value }"
            @click="activeTab = tab.value"
            :title="tab.label"
          >
            {{ tab.label }}
          </button>
        </div>
        <!-- 第二行（服务语） -->
        <div class="station-type-btns mb-3">
          <button
            v-for="tab in serviceTabs"
            :key="tab.value"
            type="button"
            class="btn btn-sm btn-outline-primary station-type-btn"
            :class="{ active: activeTab === tab.value }"
            @click="activeTab = tab.value"
            :title="tab.label"
          >
            {{ tab.label }}
          </button>
        </div>
        
        <!-- 音频序列配置 -->
        <div class="mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6>{{ getActiveTabName() }}</h6>
            <!-- 上下行不同选项 -->
            <div v-if="['arrival', 'departure', 'welcome', 'terminal'].includes(activeTab)" class="form-check">
              <input 
                class="form-check-input" 
                type="checkbox" 
                :id="'separate-' + activeTab"
                v-model="separateDirections[activeTab]"
                @change="onSeparateDirectionChange"
              >
              <label class="form-check-label" :for="'separate-' + activeTab">
                上下行不同
              </label>
            </div>
          </div>
          <div class="mb-3">
            <div class="row mb-2">
              <!-- 可用音频列表（左列） -->
              <div class="col-4">
                <label class="form-label">所有音频文件</label>
                <select ref="availableAudioSelect" class="form-select" size="10" v-model="selectedAudio">
                  <template v-for="folder in audioFolders" :key="folder.name">
                    <optgroup :label="folder.name" v-if="folder.audios && folder.audios.length > 0">
                      <option v-for="audio in folder.audios" :key="audio.name" :value="audio">
                        {{ audio.name }}
                      </option>
                    </optgroup>
                  </template>
                  <optgroup label="暂无音频文件" v-if="!audioFolders.length || audioFolders.every(f => !f.audios || !f.audios.length)">
                    <option disabled>请先选择项目文件夹</option>
                  </optgroup>
                </select>
                
                <!-- 添加和移除按钮放在可用音频列表正下方 -->
                <div class="d-flex gap-2 mt-2">
                  <button type="button" class="btn btn-primary" @click="addToSequence">
                    添加
                  </button>
                  <button type="button" class="btn btn-danger" @click="removeFromSequence" :disabled="selectedSequenceAudio === null">
                    移除
                  </button>
                </div>
              </div>
              
              <!-- 统一音频播放顺序（两列布局：可用音频列表 | 音频播放顺序） -->
              <div v-if="!(['arrival', 'departure', 'welcome', 'terminal'].includes(activeTab) && separateDirections[activeTab])" class="col-8">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <label class="form-label mb-0">音频播放顺序</label>
                  <div class="d-flex gap-2">
                    <button type="button" class="btn btn-success" @click="previewAudioSequence">
                      <i class="bi bi-play-fill"></i> 预览
                    </button>
                    <button type="button" class="btn btn-info" @click="exportUnifiedSequence" :disabled="currentSequence.length === 0 || isExportingAudio">
                      <i class="bi bi-download"></i> 导出
                    </button>
                  </div>
                </div>
                <div 
                  class="sequence-list selectable-sequence"
                  :class="{ 'selected-sequence': selectedTargetSequence === 'unified' }"
                  :style="{ height: sequenceContainerHeight + 'px' }"
                  @drop="onDrop" 
                  @dragover="onDragOver"
                  @dragenter="onDragEnter"
                  @dragleave="onDragLeave"
                  @click="selectTargetSequence('unified')"
                >
                  <div 
                    v-for="(audio, index) in currentSequence" 
                    :key="index"
                    class="sequence-item"
                    :class="{ 
                      active: selectedSequenceAudio === index,
                      'drag-over': draggedIndex !== null && draggedIndex !== index && dropIndicatorIndex === index
                    }"
                    draggable="true"
                    @dragstart="onDragStart($event, index)"
                    @click.stop="selectedSequenceAudio = index"
                  >
                    <span class="drag-handle">≡</span>
                    <span class="sequence-number">{{ index + 1 }}.</span>
                    <span class="audio-name" :title="audioFullPath(audio)">{{ audioLabel(audio) }}</span>
                  </div>
                  <!-- 拖拽辅助线 -->
                  <div 
                    v-if="dropIndicatorIndex !== null && dropIndicatorIndex === currentSequence.length" 
                    class="drop-indicator"
                  ></div>
                </div>
                <div class="form-text">按顺序播放音频，可拖拽调整顺序</div>
              </div>
              
              <!-- 上下行分别的音频播放顺序（三列布局：可用音频列表 | 上行播放顺序 | 下行播放顺序） -->
              <div v-else class="col-4">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <label class="form-label mb-0">上行音频</label>
                  <div class="d-flex gap-2">
                    <button type="button" class="btn btn-sm btn-success" @click="previewDirectionSequence('up')">
                      <i class="bi bi-play-fill"></i> 预览
                    </button>
                    <button type="button" class="btn btn-sm btn-info" @click="exportDirectionSequence('up')" :disabled="getDirectionSequence('up').length === 0 || isExportingAudio">
                      <i class="bi bi-download"></i> 导出
                    </button>
                  </div>
                </div>
                <div 
                  class="sequence-list selectable-sequence" 
                  :class="{ 'selected-sequence': selectedTargetSequence === 'up' }"
                  :style="{ height: sequenceContainerHeight + 'px' }"
                  @drop="onDirectionDrop($event, 'up')" 
                  @dragover="onDragOver"
                  @dragenter="onDirectionDragEnter($event, 'up')"
                  @dragleave="onDirectionDragLeave($event, 'up')"
                  @click="selectTargetSequence('up')"
                >
                  <div 
                    v-for="(audio, index) in getDirectionSequence('up')" 
                    :key="'up-' + index"
                    class="sequence-item"
                    :class="{ 
                      active: selectedSequenceAudio === 'up-' + index,
                      'drag-over': draggedDirection === 'up' && draggedIndex !== null && draggedIndex !== index && dropIndicatorIndex === index
                    }"
                    draggable="true"
                    @dragstart="onDirectionDragStart($event, index, 'up')"
                    @click.stop="selectedSequenceAudio = 'up-' + index; onSequenceSelectionChange('up')"
                  >
                    <span class="drag-handle">≡</span>
                    <span class="sequence-number">{{ index + 1 }}.</span>
                    <span class="audio-name" :title="audioFullPath(audio)">{{ audioLabel(audio) }}</span>
                  </div>
                  <!-- 拖拽辅助线 -->
                  <div 
                    v-if="dropIndicatorIndex !== null && dropIndicatorIndex === getDirectionSequence('up').length && draggedDirection === 'up'" 
                    class="drop-indicator"
                  ></div>
                </div>
                <div class="form-text">按顺序播放音频，可拖拽调整顺序</div>
              </div>
              
              <div v-if="['arrival', 'departure', 'welcome', 'terminal'].includes(activeTab) && separateDirections[activeTab]" class="col-4">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <label class="form-label mb-0">下行音频</label>
                  <div class="d-flex gap-2">
                    <button type="button" class="btn btn-sm btn-success" @click="previewDirectionSequence('down')">
                      <i class="bi bi-play-fill"></i> 预览
                    </button>
                    <button type="button" class="btn btn-sm btn-info" @click="exportDirectionSequence('down')" :disabled="getDirectionSequence('down').length === 0 || isExportingAudio">
                      <i class="bi bi-download"></i> 导出
                    </button>
                  </div>
                </div>
                <div 
                  class="sequence-list selectable-sequence" 
                  :class="{ 'selected-sequence': selectedTargetSequence === 'down' }"
                  :style="{ height: sequenceContainerHeight + 'px' }"
                  @drop="onDirectionDrop($event, 'down')" 
                  @dragover="onDragOver"
                  @dragenter="onDirectionDragEnter($event, 'down')"
                  @dragleave="onDirectionDragLeave($event, 'down')"
                  @click="selectTargetSequence('down')"
                >
                  <div 
                    v-for="(audio, index) in getDirectionSequence('down')" 
                    :key="'down-' + index"
                    class="sequence-item"
                    :class="{ 
                      active: selectedSequenceAudio === 'down-' + index,
                      'drag-over': draggedDirection === 'down' && draggedIndex !== null && draggedIndex !== index && dropIndicatorIndex === index
                    }"
                    draggable="true"
                    @dragstart="onDirectionDragStart($event, index, 'down')"
                    @click.stop="selectedSequenceAudio = 'down-' + index; onSequenceSelectionChange('down')"
                  >
                    <span class="drag-handle">≡</span>
                    <span class="sequence-number">{{ index + 1 }}.</span>
                    <span class="audio-name" :title="audioFullPath(audio)">{{ audioLabel(audio) }}</span>
                  </div>
                  <!-- 拖拽辅助线 -->
                  <div 
                    v-if="dropIndicatorIndex !== null && dropIndicatorIndex === getDirectionSequence('down').length && draggedDirection === 'down'" 
                    class="drop-indicator"
                  ></div>
                </div>
                <div class="form-text">按顺序播放音频，可拖拽调整顺序</div>
              </div>
            </div>
            


            <!-- 音频占位符按钮区域 -->
            <div class="mt-3">
              <h6>音频占位符</h6>
              <!-- 文件夹占位符 -->
              <template v-for="folder in audioFolders" :key="folder.name">
                <div v-if="folder.audios && folder.audios.length > 0" class="mb-2">
                  <small class="text-muted">{{ folder.name }}文件夹：</small><br>
                  <button type="button" class="btn btn-sm btn-outline-secondary me-1 mb-1" @click="addFolderPlaceholder(activeTab, folder.name, 'current')">
                    {{ folder.name }}-当前站
                  </button>
                  <button type="button" class="btn btn-sm btn-outline-secondary me-1 mb-1" @click="addFolderPlaceholder(activeTab, folder.name, 'terminal')">
                    {{ folder.name }}-终点站
                  </button>
                  <button type="button" class="btn btn-sm btn-outline-secondary me-1 mb-1" @click="addFolderPlaceholder(activeTab, folder.name, 'first')">
                    {{ folder.name }}-起点站
                  </button>
                </div>
              </template>
            </div>
            
            
          </div>
          

        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AudioTemplatePanel',
  emits: ['audioConfigChanged'],
  props: {
    audioFolders: {
      type: Array,
      default: () => []
    },
    languageSettings: {
      type: Object,
      required: true
    },
    currentDirection: {
      type: String,
      default: 'up'
    },
    defaultArrivalAudioSequence: {
      type: Array,
      default: () => []
    },
    defaultDepartureAudioSequence: {
      type: Array,
      default: () => []
    },
    welcomeAudioSequence: {
      type: Array,
      default: () => []
    },
    terminalAudioSequence: {
      type: Array,
      default: () => []
    },
    upArrivalAudioSequence: {
      type: Array,
      default: () => []
    },
    downArrivalAudioSequence: {
      type: Array,
      default: () => []
    },
    upDepartureAudioSequence: {
      type: Array,
      default: () => []
    },
    downDepartureAudioSequence: {
      type: Array,
      default: () => []
    },
    upWelcomeAudioSequence: {
      type: Array,
      default: () => []
    },
    downWelcomeAudioSequence: {
      type: Array,
      default: () => []
    },
    upTerminalAudioSequence: {
      type: Array,
      default: () => []
    },
    downTerminalAudioSequence: {
      type: Array,
      default: () => []
    },
    serviceAudioSequences: {
      type: Array,
      default: () => Array(10).fill().map(() => [])
    },
    separateDirections: {
      type: Object,
      default: () => ({
        arrival: false,
        departure: false,
        welcome: false,
        terminal: false
      })
    },
    upDirectionStations: {
      type: Array,
      default: () => []
    },
    downDirectionStations: {
      type: Array,
      default: () => []
    },
    selectedStationIndex: {
      type: Number,
      default: -1
    },
    lineType: {
      type: String,
      default: 'twoWay'
    },
    lineName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      // 使用props作为初始值，在组件内部管理本地状态
      defaultArrivalAudioSequence: [...this.defaultArrivalAudioSequence],
      defaultDepartureAudioSequence: [...this.defaultDepartureAudioSequence],
      welcomeAudioSequence: [...this.welcomeAudioSequence],
      terminalAudioSequence: [...this.terminalAudioSequence],
      // 上下行分别的音频序列
      upArrivalAudioSequence: [...this.upArrivalAudioSequence],
      downArrivalAudioSequence: [...this.downArrivalAudioSequence],
      upDepartureAudioSequence: [...this.upDepartureAudioSequence],
      downDepartureAudioSequence: [...this.downDepartureAudioSequence],
      upWelcomeAudioSequence: [...this.upWelcomeAudioSequence],
      downWelcomeAudioSequence: [...this.downWelcomeAudioSequence],
      upTerminalAudioSequence: [...this.upTerminalAudioSequence],
      downTerminalAudioSequence: [...this.downTerminalAudioSequence],
      // 上下行不同设置
      separateDirections: {...this.separateDirections},
      serviceAudioSequences: this.serviceAudioSequences.map(seq => [...seq]),
      activeTab: 'arrival',
      selectedAudio: null,
      selectedSequenceAudio: null,
      isPlaying: false,
      currentAudioIndex: -1,
      playProgress: 0,
      audioElements: [],
      stationTabs: [
        { label: '默认进站语', value: 'arrival' },
        { label: '默认出站语', value: 'departure' },
        { label: '欢迎语', value: 'welcome' },
        { label: '终点站语', value: 'terminal' },
        ...Array.from({ length: 10 }, (_, i) => ({ label: `服务语${i + 1}`, value: `service${i + 1}` }))
      ],
      audioConfigTimeout: null,
      draggedIndex: null,
      draggedDirection: null,
      dropIndicatorIndex: null,
      isPlaying: false,
      playProgress: 0,
      currentAudioIndex: -1,
      audioElements: [],
      previewSequence: null,
      previewDirection: null,
      previewSelectedStationIndex: null, // 预览时按站名在目标方向定位到的站点索引
      currentEditingDirection: 'up', // 跟踪用户当前正在编辑的方向
      expandedSequence: null, // 展开后的音频序列（用于处理占位符）
      selectedTargetSequence: 'unified', // 当前选中的目标序列：'unified', 'up', 'down'
      sequenceContainerHeight: 0, // 右侧序列容器高度，与左侧可用音频列表对齐
      isExportingAudio: false // 导出音频状态
    };
  },
  computed: {
    mainTabs() {
      return this.stationTabs.slice(0, 4);
    },
    serviceTabs() {
      return this.stationTabs.slice(4);
    },
    currentSequence() {
      // 如果当前tab支持上下行分别设置且已启用
      if (['arrival', 'departure', 'welcome', 'terminal'].includes(this.activeTab) && this.separateDirections[this.activeTab]) {
        // 返回当前方向的序列
        const direction = this.currentDirection;
        if (this.activeTab === 'arrival') {
          return direction === 'up' ? this.upArrivalAudioSequence : this.downArrivalAudioSequence;
        } else if (this.activeTab === 'departure') {
          return direction === 'up' ? this.upDepartureAudioSequence : this.downDepartureAudioSequence;
        } else if (this.activeTab === 'welcome') {
          return direction === 'up' ? this.upWelcomeAudioSequence : this.downWelcomeAudioSequence;
        } else if (this.activeTab === 'terminal') {
          return direction === 'up' ? this.upTerminalAudioSequence : this.downTerminalAudioSequence;
        }
      }
      
      // 使用统一序列
      if (this.activeTab === 'arrival') {
        return this.defaultArrivalAudioSequence;
      } else if (this.activeTab === 'departure') {
        return this.defaultDepartureAudioSequence;
      } else if (this.activeTab === 'welcome') {
        return this.welcomeAudioSequence;
      } else if (this.activeTab === 'terminal') {
        return this.terminalAudioSequence;
      } else if (this.activeTab.startsWith('service')) {
        const serviceIndex = parseInt(this.activeTab.slice(7)) - 1;
        if (serviceIndex >= 0 && serviceIndex < 10) {
          return this.serviceAudioSequences[serviceIndex];
        }
      }
      return [];
    },
    visibleLanguages() {
      if (!this.languageSettings || !this.languageSettings.languages) return [];
      const count = parseInt(this.languageSettings.count) || 1;
      return this.languageSettings.languages.slice(0, count);
    }
  },
  watch: {
    activeTab() {
      console.log('Tab切换到:', this.activeTab, '对应的上下行分别状态:', this.separateDirections[this.activeTab]);
      this.selectedSequenceAudio = null;
      this.stopPlayback();
      // 切换标签页时重置当前编辑方向为上行
      this.currentEditingDirection = 'up';
      // 根据当前标签页的上下行设置来选择目标序列
      if (['arrival', 'departure', 'welcome', 'terminal'].includes(this.activeTab) && this.separateDirections[this.activeTab]) {
        this.selectedTargetSequence = 'up';
        console.log('当前tab启用了上下行分别，选择上行');
      } else {
        this.selectedTargetSequence = 'unified';
        console.log('当前tab未启用上下行分别，选择统一序列');
      }
    },
    
    // 监听separateDirections props的变化
    separateDirections: {
      handler(newVal) {
        if (newVal && JSON.stringify(this.separateDirections) !== JSON.stringify(newVal)) {
          console.log('AudioTemplatePanel: 收到separateDirections更新:', newVal);
          this.separateDirections = { ...newVal };
        }
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    // 初始化选中的目标序列
    if (['arrival', 'departure', 'welcome', 'terminal'].includes(this.activeTab) && this.separateDirections[this.activeTab]) {
      this.selectedTargetSequence = 'up';
    } else {
      this.selectedTargetSequence = 'unified';
    }
    console.log('组件挂载后，初始化selectedTargetSequence为:', this.selectedTargetSequence);
    this.syncSequenceHeight();
    window.addEventListener('resize', this.syncSequenceHeight);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.syncSequenceHeight);
  },
  methods: {
    // 以下是从原TemplatePanel复制过来的方法，省略大部分重复代码
    getActiveTabName() {
      if (this.activeTab === 'arrival') {
        return '默认进站语音频配置';
      } else if (this.activeTab === 'departure') {
        return '默认出站语音频配置';
      } else if (this.activeTab === 'welcome') {
        return '欢迎语音频配置（第二个站的预报）';
      } else if (this.activeTab === 'terminal') {
        return '终点站语音频配置（最后一个站的进站）';
      } else if (this.activeTab.startsWith('service')) {
        return '服务语' + this.activeTab.slice(7) + '配置';
      }
      return '';
    },
    
    audioLabel(item) {
      if (typeof item === 'string' && item.startsWith('【') && item.endsWith('】')) {
        return item;
      }
      if (typeof item === 'string' && item.includes('/')) {
        return item;
      }
      for (const folder of this.audioFolders) {
        if (folder.audios) {
          const audioItem = folder.audios.find(a => a.path === item || a.url === item);
          if (audioItem) {
            return `${folder.name}/${audioItem.name}`;
          }
        }
      }
      return '未知音频';
    },

    audioFullPath(item) {
      if (typeof item === 'string' && item.includes('/')) return item;
      for (const folder of this.audioFolders) {
        if (folder.audios) {
          const audioItem = folder.audios.find(a => a.path === item || a.url === item);
          if (audioItem) return audioItem.path;
        }
      }
      return '';
    },
    
    addToSequence() {
      if (!this.selectedAudio) return;
      
      const audioPath = this.selectedAudio.path;
      
      if (['arrival', 'departure', 'welcome', 'terminal'].includes(this.activeTab) && this.separateDirections[this.activeTab]) {
        // 根据用户选中的目标序列决定方向，若未明确则默认上行
        let direction = this.selectedTargetSequence;
        if (direction === 'unified') direction = 'up';
        if (this.activeTab === 'arrival') {
          if (direction === 'up') {
            this.upArrivalAudioSequence.push(audioPath);
          } else {
            this.downArrivalAudioSequence.push(audioPath);
          }
        } else if (this.activeTab === 'departure') {
          if (direction === 'up') {
            this.upDepartureAudioSequence.push(audioPath);
          } else {
            this.downDepartureAudioSequence.push(audioPath);
          }
        } else if (this.activeTab === 'welcome') {
          if (direction === 'up') {
            this.upWelcomeAudioSequence.push(audioPath);
          } else {
            this.downWelcomeAudioSequence.push(audioPath);
          }
        } else if (this.activeTab === 'terminal') {
          if (direction === 'up') {
            this.upTerminalAudioSequence.push(audioPath);
          } else {
            this.downTerminalAudioSequence.push(audioPath);
          }
        }
        this.emitAudioConfigChange();
        return;
      }
      
      if (this.activeTab === 'arrival') {
        this.defaultArrivalAudioSequence.push(audioPath);
      } else if (this.activeTab === 'departure') {
        this.defaultDepartureAudioSequence.push(audioPath);
      } else if (this.activeTab === 'welcome') {
        this.welcomeAudioSequence.push(audioPath);
      } else if (this.activeTab === 'terminal') {
        this.terminalAudioSequence.push(audioPath);
      } else if (this.activeTab.startsWith('service')) {
        const serviceIndex = parseInt(this.activeTab.slice(7)) - 1;
        if (serviceIndex >= 0 && serviceIndex < 10) {
          this.serviceAudioSequences[serviceIndex].push(audioPath);
        }
      }
      
      this.emitAudioConfigChange();
    },
    
    addToDirectionSequence(direction) {
      if (!this.selectedAudio) return;
      const audioPath = this.selectedAudio.path;
      
      // 更新当前编辑的方向
      this.currentEditingDirection = direction;
      
      if (this.activeTab === 'arrival') {
        if (direction === 'up') {
          this.upArrivalAudioSequence.push(audioPath);
        } else {
          this.downArrivalAudioSequence.push(audioPath);
        }
      } else if (this.activeTab === 'departure') {
        if (direction === 'up') {
          this.upDepartureAudioSequence.push(audioPath);
        } else {
          this.downDepartureAudioSequence.push(audioPath);
        }
      } else if (this.activeTab === 'welcome') {
        if (direction === 'up') {
          this.upWelcomeAudioSequence.push(audioPath);
        } else {
          this.downWelcomeAudioSequence.push(audioPath);
        }
      } else if (this.activeTab === 'terminal') {
        if (direction === 'up') {
          this.upTerminalAudioSequence.push(audioPath);
        } else {
          this.downTerminalAudioSequence.push(audioPath);
        }
      }
      
      this.emitAudioConfigChange();
    },
    
    removeFromSequence() {
      if (this.selectedSequenceAudio === null) return;
      
      console.log('🗑️ 删除音频序列项:', this.selectedSequenceAudio);
      
      if (["arrival", "departure", "welcome", "terminal"].includes(this.activeTab) && this.separateDirections[this.activeTab]) {
        const parts = String(this.selectedSequenceAudio).split('-');
        if (parts.length === 2) {
          const direction = parts[0];
          const index = parseInt(parts[1]);
          let sequence = this.getDirectionSequence(direction);
          if (index >= 0 && index < sequence.length) {
            sequence.splice(index, 1);
            console.log(`✅ 已从${direction === 'up' ? '上行' : '下行'}${this.activeTab}序列中删除`);
          }
        }
        this.selectedSequenceAudio = null;
        // 删除操作后立即强制同步到父组件
        this.forceSyncAudioConfig();
        return;
      }
      
      if (this.activeTab === 'arrival') {
        this.defaultArrivalAudioSequence.splice(this.selectedSequenceAudio, 1);
        console.log('✅ 已从默认进站语序列中删除');
      } else if (this.activeTab === 'departure') {
        this.defaultDepartureAudioSequence.splice(this.selectedSequenceAudio, 1);
        console.log('✅ 已从默认出站语序列中删除');
      } else if (this.activeTab === 'welcome') {
        this.welcomeAudioSequence.splice(this.selectedSequenceAudio, 1);
        console.log('✅ 已从欢迎语序列中删除');
      } else if (this.activeTab === 'terminal') {
        this.terminalAudioSequence.splice(this.selectedSequenceAudio, 1);
        console.log('✅ 已从终点站语序列中删除');
      } else if (this.activeTab.startsWith('service')) {
        const serviceIndex = parseInt(this.activeTab.slice(7)) - 1;
        if (serviceIndex >= 0 && serviceIndex < 10) {
          this.serviceAudioSequences[serviceIndex].splice(this.selectedSequenceAudio, 1);
          console.log(`✅ 已从服务语${serviceIndex + 1}序列中删除`);
        }
      }
      
      this.selectedSequenceAudio = null;
      // 删除操作后立即强制同步到父组件
      this.forceSyncAudioConfig();
    },
    
    previewAudioSequence() {
      if (this.isPlaying) {
        this.stopPlayback();
        return;
      }
      
      if (this.currentSequence.length === 0) {
        alert('请先添加音频到播放序列');
        return;
      }
      
      // 重置展开序列，确保重新展开
      this.expandedSequence = null;
      
      // 真正的音频播放逻辑
      this.isPlaying = true;
      this.playProgress = 0;
      this.currentAudioIndex = 0;
      this.audioElements = [];
      
      // 创建音频元素并播放
      this.playNextAudio();
    },
    
    playNextAudio() {
      this.playNextAudioFromSequence(this.currentSequence);
    },
    
    playNextAudioFromSequence(originalSequence) {
      // 如果还没有展开过，先展开序列
      if (!this.expandedSequence) {
        this.expandedSequence = this.expandAudioSequence(originalSequence);
        console.log('报站模板：展开后的音频序列:', this.expandedSequence);
        this.currentAudioIndex = 0;
      }
      
      if (this.currentAudioIndex >= this.expandedSequence.length) {
        // 播放完成
        this.isPlaying = false;
        this.playProgress = 100;
        this.expandedSequence = null; // 重置展开序列
        setTimeout(() => {
          this.playProgress = 0;
          this.previewSequence = null;
          this.previewDirection = null;
          this.previewSelectedStationIndex = null;
        }, 1000);
        return;
      }
      
      const audioItem = this.expandedSequence[this.currentAudioIndex];
      console.log('报站模板：处理音频项:', audioItem);
      
      // 查找音频文件：仅允许按 path/url 精确匹配
      let audioFile = null;
      for (const folder of this.audioFolders) {
        if (folder.audios) {
          audioFile = folder.audios.find(a => a.path === audioItem || a.url === audioItem);
          if (audioFile) break;
        }
      }
      
      if (audioFile) {
        console.log('报站模板：找到音频文件:', audioFile);
        const audio = new Audio(audioFile.url);
        this.audioElements.push(audio);
        
        audio.addEventListener('ended', () => {
          this.currentAudioIndex++;
          this.playProgress = (this.currentAudioIndex / this.expandedSequence.length) * 100;
          this.playNextAudioFromSequence(originalSequence);
        });
        
        audio.addEventListener('error', () => {
          console.error('音频播放失败:', audioFile.name);
          this.currentAudioIndex++;
          this.playProgress = (this.currentAudioIndex / this.expandedSequence.length) * 100;
          this.playNextAudioFromSequence(originalSequence);
        });
        
        audio.play().catch(error => {
          console.error('音频播放失败:', error);
          this.currentAudioIndex++;
          this.playProgress = (this.currentAudioIndex / this.expandedSequence.length) * 100;
          this.playNextAudioFromSequence(originalSequence);
        });
      } else {
        // 找不到音频文件，跳过
        console.warn('找不到音频文件:', audioItem);
        this.currentAudioIndex++;
        this.playProgress = (this.currentAudioIndex / this.expandedSequence.length) * 100;
        this.playNextAudioFromSequence(originalSequence);
      }
    },

    // 展开音频序列，将占位符替换为实际的音频路径
    expandAudioSequence(sequence) {
      const result = [];
      
      for (const item of sequence) {
        if (typeof item === 'string' && item.startsWith('【') && item.endsWith('】')) {
          console.log('报站模板：展开占位符:', item);
          
          // 解析文件夹名
          const folderMatch = item.match(/【(.+?)-/);
          const folderName = folderMatch ? folderMatch[1] : '';
          console.log('报站模板：解析文件夹名:', folderName);
          
          // 获取目标文件夹
          const targetFolder = this.audioFolders.find(f => f.name === folderName);
          console.log('报站模板：找到目标文件夹:', targetFolder);
          
          // 解析站点类型并获取目标站点
          let targetStationName = '';
          let targetStation = null;
          
          // 获取当前站点信息
          const currentStations = this.previewDirection === 'up' ? this.upDirectionStations : 
                                 this.previewDirection === 'down' ? this.downDirectionStations :
                                 (this.currentDirection === 'up' ? this.upDirectionStations : this.downDirectionStations);
          
          if (!currentStations || currentStations.length === 0) {
            console.warn('报站模板：没有可用的站点信息来解析占位符');
            continue;
          }
          
          // 获取当前站点、起点站、终点站
          let currentIndex = 0;
          if (this.activeTab === 'welcome') {
            currentIndex = 0; // 欢迎语固定使用首站作为“当前站”
          } else if (this.activeTab === 'terminal') {
            currentIndex = currentStations.length - 1; // 终点站语固定使用末站作为“当前站”
          } else {
            // 如果存在按站名映射得到的预览索引，优先使用
            if (typeof this.previewSelectedStationIndex === 'number' && this.previewSelectedStationIndex >= 0 && this.previewSelectedStationIndex < currentStations.length) {
              currentIndex = this.previewSelectedStationIndex;
            } else {
              currentIndex = this.selectedStationIndex >= 0 && this.selectedStationIndex < currentStations.length 
                            ? this.selectedStationIndex : 0;
            }
          }
          
          if (item.includes('当前站')) {
            targetStation = currentStations[currentIndex];
            targetStationName = targetStation.name;
            console.log('报站模板：当前站 - targetStation:', targetStation);
          } else if (item.includes('起点站')) {
            targetStation = currentStations[0];
            targetStationName = targetStation.name;
            console.log('报站模板：起点站 - targetStation:', targetStation);
          } else if (item.includes('终点站')) {
            targetStation = currentStations[currentStations.length - 1];
            targetStationName = targetStation.name;
            console.log('报站模板：终点站 - targetStation:', targetStation);
          }
          
          // *** 优先级1：检查目标站点是否有自定义播放列表 ***
          if (targetStation && targetStation.stationAudioSequence && targetStation.stationAudioSequence.length > 0) {
            console.log('报站模板：找到自定义播放列表:', targetStation.stationAudioSequence);
            // 将整个自定义播放列表添加到结果中
            result.push(...targetStation.stationAudioSequence);
          } else {
            // *** 优先级2：如果没有自定义播放列表，按站点名查找单个音频文件 ***
            console.log('报站模板：没有自定义播放列表，按站点名查找:', targetStationName);
            if (targetStationName && targetFolder && targetFolder.audios) {
              const audio = targetFolder.audios.find(a => a.name.replace(/\.(mp3|wav|ogg|m4a)$/i, '') === targetStationName);
              if (audio) {
                console.log('报站模板：找到站点名对应音频:', audio.path);
                result.push(audio.path);
              } else {
                console.log('报站模板：未找到站点名对应音频');
              }
            }
          }
        } else {
          // 不是占位符，直接添加
          result.push(item);
        }
      }
      
      return result;
    },
    
    // 解析占位符为实际的音频文件路径
    resolvePlaceholder(placeholder) {
      // 获取当前站点信息
      const currentStations = this.previewDirection === 'up' ? this.upDirectionStations : 
                             this.previewDirection === 'down' ? this.downDirectionStations :
                             (this.currentDirection === 'up' ? this.upDirectionStations : this.downDirectionStations);
      
      if (!currentStations || currentStations.length === 0) {
        console.warn('没有可用的站点信息来解析占位符');
        return null;
      }
      
      // 获取当前站点、起点站、终点站
      let currentStationIndex = 0;
      if (this.activeTab === 'welcome') {
        currentStationIndex = 0; // 欢迎语固定使用首站作为“当前站”
      } else if (this.activeTab === 'terminal') {
        currentStationIndex = currentStations.length - 1; // 终点站语固定使用末站作为“当前站”
      } else {
        // 如果存在按站名映射得到的预览索引，优先使用
        if (typeof this.previewSelectedStationIndex === 'number' && this.previewSelectedStationIndex >= 0 && this.previewSelectedStationIndex < currentStations.length) {
          currentStationIndex = this.previewSelectedStationIndex;
        } else {
          currentStationIndex = this.selectedStationIndex >= 0 && this.selectedStationIndex < currentStations.length 
                              ? this.selectedStationIndex : 0;
        }
      }
      const currentStation = currentStations[currentStationIndex];
      const firstStation = currentStations[0];
      const terminalStation = currentStations[currentStations.length - 1];
      
      // 解析不同类型的占位符
      if (placeholder === '【当前站】') {
        return this.findStationAudio(currentStation, 'current');
      } else if (placeholder === '【起点站】') {
        return this.findStationAudio(firstStation, 'first');
      } else if (placeholder === '【终点站】') {
        return this.findStationAudio(terminalStation, 'terminal');
      } else if (placeholder === '【副站名】') {
        // 处理副站名
        if (currentStation && currentStation.useAlias && currentStation.aliasSequence && currentStation.aliasSequence.length > 0) {
          return currentStation.aliasSequence[0]; // 取第一个副站名音频
        }
      } else if (placeholder.includes('-')) {
        // 处理文件夹占位符，如【站名-当前站】
        const match = placeholder.match(/【(.+?)-(.+?)】/);
        if (match) {
          const folderName = match[1];
          const stationType = match[2];
          
          let targetStation = null;
          if (stationType === '当前站') {
            targetStation = currentStation;
          } else if (stationType === '起点站') {
            targetStation = firstStation;
          } else if (stationType === '终点站') {
            targetStation = terminalStation;
          }
          
          if (targetStation) {
            return this.findStationAudioInFolder(targetStation, folderName);
          }
        }
      }
      
      return null;
    },
    
    // 在指定文件夹中查找站点对应的音频
    findStationAudioInFolder(station, folderName) {
      const folder = this.audioFolders.find(f => f.name === folderName);
      if (!folder || !folder.audios) {
        return null;
      }
      
      // 尝试匹配站点名称
      const stationName = station.name;
      if (!stationName) return null;
      
      // 精确匹配
      let audio = folder.audios.find(a => a.name === stationName || a.name === stationName + '.wav' || a.name === stationName + '.mp3');
      if (audio) return audio.path || audio.url;
      
      // 部分匹配
      audio = folder.audios.find(a => a.name.includes(stationName) || stationName.includes(a.name.replace(/\.(wav|mp3|ogg|m4a)$/i, '')));
      if (audio) return audio.path || audio.url;
      
      return null;
    },
    
    // 查找站点音频（通用）
    findStationAudio(station, type) {
      if (!station) return null;
      
      // 如果站点有自定义的多语言序列，优先使用
      if (station.multiLanguageSequence && station.multiLanguageSequence.length > 0) {
        return station.multiLanguageSequence[0];
      }
      
      // 否则尝试在各个文件夹中查找
      for (const folder of this.audioFolders) {
        const audio = this.findStationAudioInFolder(station, folder.name);
        if (audio) return audio;
      }
      
      return null;
    },
    
    previewDirectionSequence(direction) {
      const sequence = this.getDirectionSequence(direction);
      if (!sequence || sequence.length === 0) {
        alert('请先添加音频到' + (direction === 'up' ? '上行' : '下行') + '播放序列');
        return;
      }
      
      if (this.isPlaying) {
        this.stopPlayback();
        return;
      }
      
      // 当启用上下行分别设置且预览方向与当前方向不一致时，按站名在目标方向定位
      if (['arrival', 'departure', 'welcome', 'terminal'].includes(this.activeTab) && this.separateDirections[this.activeTab] && direction !== this.currentDirection) {
        const sourceStations = this.currentDirection === 'up' ? this.upDirectionStations : this.downDirectionStations;
        const targetStations = direction === 'up' ? this.upDirectionStations : this.downDirectionStations;
        const srcIndex = this.selectedStationIndex;
        if (Array.isArray(sourceStations) && Array.isArray(targetStations) && srcIndex >= 0 && srcIndex < sourceStations.length) {
          const sourceStation = sourceStations[srcIndex];
          const sourceName = sourceStation && sourceStation.name ? sourceStation.name : '';
          if (sourceName) {
            const matchedIndex = targetStations.findIndex(s => s && s.name === sourceName);
            if (matchedIndex === -1) {
              alert((direction === 'up' ? '上行' : '下行') + '方向中不存在同名站点【' + sourceName + '】，无法预览');
              return;
            }
            this.previewSelectedStationIndex = matchedIndex;
          }
        }
      }
      
      // 重置展开序列，确保重新展开
      this.expandedSequence = null;
      
      // 设置当前序列为指定方向的序列
      this.previewSequence = sequence;
      this.previewDirection = direction;
      
      // 真正的音频播放逻辑
      this.isPlaying = true;
      this.playProgress = 0;
      this.currentAudioIndex = 0;
      this.audioElements = [];
      
      // 创建音频元素并播放
      this.playNextAudioFromSequence(sequence);
    },
    
    stopPlayback() {
      this.isPlaying = false;
      this.currentAudioIndex = -1;
      this.playProgress = 0;
      this.expandedSequence = null; // 重置展开序列
      this.previewSelectedStationIndex = null;
      
      // 停止所有正在播放的音频
      this.audioElements.forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
      this.audioElements = [];
    },
    
    getDirectionSequence(direction) {
      if (this.activeTab === 'arrival') {
        return direction === 'up' ? this.upArrivalAudioSequence : this.downArrivalAudioSequence;
      } else if (this.activeTab === 'departure') {
        return direction === 'up' ? this.upDepartureAudioSequence : this.downDepartureAudioSequence;
      } else if (this.activeTab === 'welcome') {
        return direction === 'up' ? this.upWelcomeAudioSequence : this.downWelcomeAudioSequence;
      } else if (this.activeTab === 'terminal') {
        return direction === 'up' ? this.upTerminalAudioSequence : this.downTerminalAudioSequence;
      }
      return [];
    },
    
    onSeparateDirectionChange() {
      // 重要：使用nextTick确保DOM更新后再读取值
      this.$nextTick(() => {
        console.log('=== onSeparateDirectionChange触发 ===');
        console.log('当前tab:', this.activeTab);
        console.log('勾选状态:', this.separateDirections[this.activeTab]);
        console.log('完整的separateDirections:', JSON.stringify(this.separateDirections));
        console.log('复选框DOM元素checked状态:', document.getElementById('separate-' + this.activeTab)?.checked);
        
        // 强制检查复选框的实际状态
        const checkboxElement = document.getElementById('separate-' + this.activeTab);
        const actualChecked = checkboxElement ? checkboxElement.checked : false;
        console.log('复选框实际checked状态:', actualChecked);
        
        // 如果DOM状态和data状态不一致，强制同步
        if (actualChecked !== this.separateDirections[this.activeTab]) {
          console.log('检测到状态不一致，强制同步...');
          this.separateDirections[this.activeTab] = actualChecked;
        }
        
      if (this.separateDirections[this.activeTab]) {
          console.log('✅ 启用上下行分别，选择上行序列');
        const currentSeq = [...this.currentSequence];
        if (this.activeTab === 'arrival') {
          this.upArrivalAudioSequence = [...currentSeq];
          this.downArrivalAudioSequence = [...currentSeq];
        } else if (this.activeTab === 'departure') {
          this.upDepartureAudioSequence = [...currentSeq];
          this.downDepartureAudioSequence = [...currentSeq];
        } else if (this.activeTab === 'welcome') {
          this.upWelcomeAudioSequence = [...currentSeq];
          this.downWelcomeAudioSequence = [...currentSeq];
        } else if (this.activeTab === 'terminal') {
          this.upTerminalAudioSequence = [...currentSeq];
          this.downTerminalAudioSequence = [...currentSeq];
        }
          // 启用上下行分别时，默认选中上行
          this.selectedTargetSequence = 'up';
        } else {
          console.log('❌ 禁用上下行分别，选择统一序列');
          // 禁用上下行分别时，选中统一序列
          this.selectedTargetSequence = 'unified';
      }
      this.selectedSequenceAudio = null;
      this.emitAudioConfigChange();
      });
    },
    
    onSequenceSelectionChange(direction) {
      // 更新当前编辑的方向
      this.currentEditingDirection = direction;
    },
    
    addPresetAudio(type, preset) {
      let placeholder = '';
      
      if (preset.startsWith('current')) {
        const langIndex = preset.length > 7 ? parseInt(preset.substring(7)) : 0;
        if (langIndex > 0) {
          placeholder = `【当前站(${this.languageSettings.languages[langIndex-1].name})】`;
        } else {
          placeholder = '【当前站】';
        }
      } else if (preset.startsWith('terminal')) {
        const langIndex = preset.length > 8 ? parseInt(preset.substring(8)) : 0;
        if (langIndex > 0) {
          placeholder = `【终点站(${this.languageSettings.languages[langIndex-1].name})】`;
        } else {
          placeholder = '【终点站】';
        }
      } else if (preset.startsWith('first')) {
        const langIndex = preset.length > 5 ? parseInt(preset.substring(5)) : 0;
        if (langIndex > 0) {
          placeholder = `【起点站(${this.languageSettings.languages[langIndex-1].name})】`;
        } else {
          placeholder = '【起点站】';
        }
      } else if (preset === 'alias') {
        placeholder = '【副站名】';
      }

      // 检查当前是否启用上下行分别设置
      if (['arrival', 'departure', 'welcome', 'terminal'].includes(type) && this.separateDirections[type]) {
        // 根据用户当前编辑的方向来决定添加到哪个序列
        let direction = this.currentEditingDirection;
        
        // 如果有选中的序列项，根据选中项判断方向
        if (this.selectedSequenceAudio !== null && typeof this.selectedSequenceAudio === 'string') {
          if (this.selectedSequenceAudio.startsWith('up-')) {
            direction = 'up';
          } else if (this.selectedSequenceAudio.startsWith('down-')) {
            direction = 'down';
          }
        }
        
        if (type === 'arrival') {
          if (direction === 'up') {
            this.upArrivalAudioSequence.push(placeholder);
          } else {
            this.downArrivalAudioSequence.push(placeholder);
          }
        } else if (type === 'departure') {
          if (direction === 'up') {
            this.upDepartureAudioSequence.push(placeholder);
          } else {
            this.downDepartureAudioSequence.push(placeholder);
          }
        } else if (type === 'welcome') {
          if (direction === 'up') {
            this.upWelcomeAudioSequence.push(placeholder);
          } else {
            this.downWelcomeAudioSequence.push(placeholder);
          }
        } else if (type === 'terminal') {
          if (direction === 'up') {
            this.upTerminalAudioSequence.push(placeholder);
          } else {
            this.downTerminalAudioSequence.push(placeholder);
          }
        }
      } else {
        // 添加到统一序列
        if (type === 'arrival') {
          this.defaultArrivalAudioSequence.push(placeholder);
        } else if (type === 'departure') {
          this.defaultDepartureAudioSequence.push(placeholder);
        } else if (type === 'welcome') {
          this.welcomeAudioSequence.push(placeholder);
        } else if (type === 'terminal') {
          this.terminalAudioSequence.push(placeholder);
        } else if (type.startsWith('service')) {
          const serviceIndex = parseInt(type.slice(7)) - 1;
          if (serviceIndex >= 0 && serviceIndex < 10) {
            this.serviceAudioSequences[serviceIndex].push(placeholder);
          }
        }
      }
      
      this.emitAudioConfigChange();
    },
    
    addFolderPlaceholder(type, folderName, stationType) {
      let placeholder = `【${folderName}-${stationType === 'current' ? '当前站' : stationType === 'terminal' ? '终点站' : '起点站'}】`;

      console.log('添加占位符:', placeholder, '当前tab:', type, '选中的目标序列:', this.selectedTargetSequence, '是否启用上下行分别:', this.separateDirections[type]);

      // 检查当前是否启用上下行分别设置
      if (['arrival', 'departure', 'welcome', 'terminal'].includes(type) && this.separateDirections[type]) {
        // 根据当前选中的目标序列来决定添加到哪个序列
        let direction = this.selectedTargetSequence;
        
        // 如果选中的是 'unified'，默认添加到上行
        if (direction === 'unified') {
            direction = 'up';
        }
        
        console.log('上下行分别模式，添加到方向:', direction);
        
        if (type === 'arrival') {
          if (direction === 'up') {
            this.upArrivalAudioSequence.push(placeholder);
            console.log('已添加到上行进站序列，当前序列:', this.upArrivalAudioSequence);
          } else {
            this.downArrivalAudioSequence.push(placeholder);
            console.log('已添加到下行进站序列，当前序列:', this.downArrivalAudioSequence);
          }
        } else if (type === 'departure') {
          if (direction === 'up') {
            this.upDepartureAudioSequence.push(placeholder);
            console.log('已添加到上行出站序列，当前序列:', this.upDepartureAudioSequence);
          } else {
            this.downDepartureAudioSequence.push(placeholder);
            console.log('已添加到下行出站序列，当前序列:', this.downDepartureAudioSequence);
          }
        } else if (type === 'welcome') {
          if (direction === 'up') {
            this.upWelcomeAudioSequence.push(placeholder);
            console.log('已添加到上行欢迎语序列，当前序列:', this.upWelcomeAudioSequence);
          } else {
            this.downWelcomeAudioSequence.push(placeholder);
            console.log('已添加到下行欢迎语序列，当前序列:', this.downWelcomeAudioSequence);
          }
        } else if (type === 'terminal') {
          if (direction === 'up') {
            this.upTerminalAudioSequence.push(placeholder);
            console.log('已添加到上行终点站序列，当前序列:', this.upTerminalAudioSequence);
          } else {
            this.downTerminalAudioSequence.push(placeholder);
            console.log('已添加到下行终点站序列，当前序列:', this.downTerminalAudioSequence);
          }
        }
      } else {
        // 添加到统一序列
        if (type === 'arrival') {
          this.defaultArrivalAudioSequence.push(placeholder);
        } else if (type === 'departure') {
          this.defaultDepartureAudioSequence.push(placeholder);
        } else if (type === 'welcome') {
          this.welcomeAudioSequence.push(placeholder);
        } else if (type === 'terminal') {
          this.terminalAudioSequence.push(placeholder);
        } else if (type.startsWith('service')) {
          const serviceIndex = parseInt(type.slice(7)) - 1;
          if (serviceIndex >= 0 && serviceIndex < 10) {
            this.serviceAudioSequences[serviceIndex].push(placeholder);
          }
        }
      }
      
      this.emitAudioConfigChange();
    },
    
    addFolderPlaceholderToDirection(type, folderName, stationType, direction) {
      let placeholder = `【${folderName}-${stationType === 'current' ? '当前站' : stationType === 'terminal' ? '终点站' : '起点站'}】`;

      if (type === 'arrival') {
        if (direction === 'up') {
          this.upArrivalAudioSequence.push(placeholder);
        } else {
          this.downArrivalAudioSequence.push(placeholder);
        }
      } else if (type === 'departure') {
        if (direction === 'up') {
          this.upDepartureAudioSequence.push(placeholder);
        } else {
          this.downDepartureAudioSequence.push(placeholder);
        }
      } else if (type === 'welcome') {
        if (direction === 'up') {
          this.upWelcomeAudioSequence.push(placeholder);
        } else {
          this.downWelcomeAudioSequence.push(placeholder);
        }
      } else if (type === 'terminal') {
        if (direction === 'up') {
          this.upTerminalAudioSequence.push(placeholder);
        } else {
          this.downTerminalAudioSequence.push(placeholder);
        }
      }
      
      this.emitAudioConfigChange();
    },
    
    // 拖拽相关方法
    onDragStart(event, index) {
      this.draggedIndex = index;
      event.dataTransfer.effectAllowed = 'move';
    },
    
    onDirectionDragStart(event, index, direction) {
      this.draggedIndex = index;
      this.draggedDirection = direction;
      event.dataTransfer.effectAllowed = 'move';
    },
    
    onDragOver(event) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      this.updateDropIndicator(event);
    },
    
    onDragEnter(event) {
      event.preventDefault();
      this.updateDropIndicator(event);
    },
    
    onDragLeave(event) {
      // 只有当真正离开容器时才清除指示器
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.dropIndicatorIndex = null;
      }
    },
    
    onDirectionDragEnter(event, direction) {
      event.preventDefault();
      this.updateDirectionDropIndicator(event, direction);
    },
    
    onDirectionDragLeave(event, direction) {
      // 只有当真正离开容器时才清除指示器
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.dropIndicatorIndex = null;
      }
    },
    
    updateDropIndicator(event) {
      const dropIndex = this.getDropIndex(event);
      this.dropIndicatorIndex = dropIndex;
    },
    
    updateDirectionDropIndicator(event, direction) {
      const dropIndex = this.getDropIndex(event);
      this.dropIndicatorIndex = dropIndex;
    },
    
    onDrop(event) {
      event.preventDefault();
      const dropIndex = this.getDropIndex(event);
      if (this.draggedIndex !== null && dropIndex !== null && this.draggedIndex !== dropIndex) {
        this.moveItem(this.draggedIndex, dropIndex);
      }
      this.draggedIndex = null;
      this.dropIndicatorIndex = null;
    },
    
    onDirectionDrop(event, direction) {
      event.preventDefault();
      const dropIndex = this.getDropIndex(event);
      if (this.draggedIndex !== null && dropIndex !== null && this.draggedDirection === direction && this.draggedIndex !== dropIndex) {
        this.moveDirectionItem(this.draggedIndex, dropIndex, direction);
      }
      this.draggedIndex = null;
      this.draggedDirection = null;
      this.dropIndicatorIndex = null;
    },
    
    getDropIndex(event) {
      const items = event.currentTarget.querySelectorAll('.sequence-item');
      const rect = event.currentTarget.getBoundingClientRect();
      const y = event.clientY - rect.top;
      
      for (let i = 0; i < items.length; i++) {
        const itemRect = items[i].getBoundingClientRect();
        const itemY = itemRect.top - rect.top + itemRect.height / 2;
        if (y < itemY) {
          return i;
        }
      }
      return items.length;
    },
    
    moveItem(fromIndex, toIndex) {
      const sequence = this.currentSequence;
      const item = sequence.splice(fromIndex, 1)[0];
      sequence.splice(toIndex, 0, item);
      this.emitAudioConfigChange();
    },
    
    moveDirectionItem(fromIndex, toIndex, direction) {
      const sequence = this.getDirectionSequence(direction);
      const item = sequence.splice(fromIndex, 1)[0];
      sequence.splice(toIndex, 0, item);
      this.emitAudioConfigChange();
    },
    
    emitAudioConfigChange() {
      clearTimeout(this.audioConfigTimeout);
      this.audioConfigTimeout = setTimeout(() => {
        this.$emit('audioConfigChanged', {
          defaultArrivalAudioSequence: this.defaultArrivalAudioSequence,
          defaultDepartureAudioSequence: this.defaultDepartureAudioSequence,
          welcomeAudioSequence: this.welcomeAudioSequence,
          terminalAudioSequence: this.terminalAudioSequence,
          upArrivalAudioSequence: this.upArrivalAudioSequence,
          downArrivalAudioSequence: this.downArrivalAudioSequence,
          upDepartureAudioSequence: this.upDepartureAudioSequence,
          downDepartureAudioSequence: this.downDepartureAudioSequence,
          upWelcomeAudioSequence: this.upWelcomeAudioSequence,
          downWelcomeAudioSequence: this.downWelcomeAudioSequence,
          upTerminalAudioSequence: this.upTerminalAudioSequence,
          downTerminalAudioSequence: this.downTerminalAudioSequence,
          serviceAudioSequences: this.serviceAudioSequences,
          separateDirections: this.separateDirections
        });
      }, 500);
    },

    // 立即同步音频配置（不等待延迟），用于删除操作和导出前确保数据最新
    forceSyncAudioConfig() {
      console.log('🔄 强制同步音频配置到父组件...');
      clearTimeout(this.audioConfigTimeout);
      this.$emit('audioConfigChanged', {
        defaultArrivalAudioSequence: this.defaultArrivalAudioSequence,
        defaultDepartureAudioSequence: this.defaultDepartureAudioSequence,
        welcomeAudioSequence: this.welcomeAudioSequence,
        terminalAudioSequence: this.terminalAudioSequence,
        upArrivalAudioSequence: this.upArrivalAudioSequence,
        downArrivalAudioSequence: this.downArrivalAudioSequence,
        upDepartureAudioSequence: this.upDepartureAudioSequence,
        downDepartureAudioSequence: this.downDepartureAudioSequence,
        upWelcomeAudioSequence: this.upWelcomeAudioSequence,
        downWelcomeAudioSequence: this.downWelcomeAudioSequence,
        upTerminalAudioSequence: this.upTerminalAudioSequence,
        downTerminalAudioSequence: this.downTerminalAudioSequence,
        serviceAudioSequences: this.serviceAudioSequences,
        separateDirections: this.separateDirections
      });
      console.log('✅ 音频配置已同步', {
        welcomeAudioSequence: this.welcomeAudioSequence,
        terminalAudioSequence: this.terminalAudioSequence,
        upWelcomeAudioSequence: this.upWelcomeAudioSequence,
        downWelcomeAudioSequence: this.downWelcomeAudioSequence,
        upTerminalAudioSequence: this.upTerminalAudioSequence,
        downTerminalAudioSequence: this.downTerminalAudioSequence
      });
    },

    // 选择目标序列
    selectTargetSequence(target) {
      this.selectedTargetSequence = target;
      console.log('选择目标序列:', target);
    },

    // 当序列选择改变时，同时更新目标序列
    onSequenceSelectionChange(direction) {
      this.selectedTargetSequence = direction;
      console.log('序列选择改变，目标序列更新为:', direction);
    },

    // 同步右侧序列容器高度与左侧可用音频列表
    syncSequenceHeight() {
      this.$nextTick(() => {
        const sel = this.$refs.availableAudioSelect;
        if (sel && sel.offsetHeight) {
          this.sequenceContainerHeight = sel.offsetHeight;
        }
      });
    },

    // 导出统一序列
    async exportUnifiedSequence() {
      if (!this.currentSequence || this.currentSequence.length === 0) {
        alert('请先添加音频到播放序列');
        return;
      }
      
      // 检查是否有选中的站点
      if (this.selectedStationIndex < 0) {
        alert('请先在左侧选择一个站点作为导出基准');
        return;
      }
      
      const templateType = this.getActiveTabName().replace(/音频配置|（.*）/g, '');
      const fileName = `${this.lineName || '默认'}-${templateType}.wav`;
      await this.exportAudioSequence(this.currentSequence, fileName);
    },

    // 导出方向序列
    async exportDirectionSequence(direction) {
      const sequence = this.getDirectionSequence(direction);
      if (!sequence || sequence.length === 0) {
        alert(`请先添加音频到${direction === 'up' ? '上行' : '下行'}播放序列`);
        return;
      }
      
      // 检查是否有选中的站点
      if (this.selectedStationIndex < 0) {
        alert('请先在左侧选择一个站点作为导出基准');
        return;
      }
      
      const templateType = this.getActiveTabName().replace(/音频配置|（.*）/g, '');
      const directionText = direction === 'up' ? '上行' : '下行';
      const fileName = `${this.lineName || '默认'}-${templateType}-${directionText}.wav`;
      await this.exportAudioSequence(sequence, fileName, direction);
    },

    // 导出音频序列（核心方法）
    async exportAudioSequence(sequence, defaultFileName, direction = null) {
      if (this.isExportingAudio) return;
      this.isExportingAudio = true;
      try {
        // 设置预览方向（用于占位符解析）
        if (direction) {
          this.previewDirection = direction;
        }
        
        const expandedSequence = this.expandAudioSequence(sequence);
        const audioBuffers = await this.loadAudioBuffers(expandedSequence);
        if (!audioBuffers || audioBuffers.length === 0) {
          alert('无法找到有效的音频文件进行导出');
          return;
        }

        const mergedBuffer = await this.mergeAudioBuffers(audioBuffers);
        if (!mergedBuffer) {
          alert('音频合并失败');
          return;
        }

        const wavBlob = this.encodeWav(mergedBuffer);
        if (!wavBlob) {
          alert('音频编码失败');
          return;
        }

        const fileName = defaultFileName || 'export.wav';
        this.triggerDownload(wavBlob, fileName);
      } catch (error) {
        console.error('音频导出失败:', error);
        alert('音频导出失败：' + (error && error.message ? error.message : '未知错误'));
      } finally {
        this.isExportingAudio = false;
        this.previewDirection = null;
      }
    },

    // 加载音频缓冲区
    async loadAudioBuffers(sequence) {
      if (!sequence || sequence.length === 0) return [];

      const ContextClass = window.AudioContext || window.webkitAudioContext;
      if (!ContextClass) {
        alert('浏览器不支持导出音频');
        return [];
      }

      const audioContext = new ContextClass();
      const buffers = [];
      let referenceSampleRate = null;

      for (const item of sequence) {
        const url = this.getAudioUrl(item);
        if (!url || (typeof url === 'string' && url.startsWith('【'))) {
          continue;
        }

        try {
          const arrayBuffer = await this.fetchAudioData(url);
          if (!arrayBuffer) continue;
          const decoded = await audioContext.decodeAudioData(arrayBuffer.slice(0));
          if (!decoded) continue;

          if (referenceSampleRate === null) {
            referenceSampleRate = decoded.sampleRate;
          }

          let processedBuffer = decoded;
          if (decoded.sampleRate !== referenceSampleRate) {
            processedBuffer = await this.resampleAudioBuffer(decoded, referenceSampleRate);
          }

          if (processedBuffer) {
            buffers.push(processedBuffer);
          }
        } catch (error) {
          console.warn('加载音频失败，已跳过', item, error);
        }
      }

      try {
        if (audioContext && audioContext.state !== 'closed') {
          await audioContext.close();
        }
      } catch (closeError) {
        console.warn('关闭AudioContext失败', closeError);
      }

      return buffers;
    },

    // 获取音频URL
    getAudioUrl(item) {
      // 如果是占位符，返回占位符本身，让调用方处理
      if (typeof item === 'string' && item.startsWith('【') && item.endsWith('】')) {
        return item; // 返回占位符，让调用方特殊处理
      }
      
      // 如果是路径格式，查找对应的URL
      if (typeof item === 'string' && item.includes('/')) {
        // 仅允许按 path 精确匹配，避免同名串目录
        for (const folder of this.audioFolders) {
          if (folder.audios) {
            const audio = folder.audios.find(a => a.path === item);
            if (audio) return audio.url;
          }
        }
        return null;
      }
      
      // 如果是blob URL，直接返回
      return item;
    },

    // 重采样音频缓冲区
    async resampleAudioBuffer(audioBuffer, targetSampleRate) {
      try {
        const OfflineContext = window.OfflineAudioContext || window.webkitOfflineAudioContext;
        if (!OfflineContext) return audioBuffer;
        const length = Math.ceil(audioBuffer.length * targetSampleRate / audioBuffer.sampleRate);
        const offlineContext = new OfflineContext(audioBuffer.numberOfChannels, length, targetSampleRate);
        const source = offlineContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(offlineContext.destination);
        source.start(0);
        return offlineContext.startRendering();
      } catch (error) {
        console.warn('重采样失败，使用原始音频', error);
        return audioBuffer;
      }
    },

    // 获取音频数据
    async fetchAudioData(url) {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('网络请求失败: ' + response.statusText);
        }
        return await response.arrayBuffer();
      } catch (error) {
        console.warn('获取音频数据失败', url, error);
        return null;
      }
    },

    // 合并音频缓冲区
    async mergeAudioBuffers(buffers) {
      if (!buffers || buffers.length === 0) return null;

      const ContextClass = window.AudioContext || window.webkitAudioContext;
      if (!ContextClass) {
        alert('浏览器不支持导出音频');
        return null;
      }

      const tempContext = new ContextClass();
      const sampleRate = buffers[0].sampleRate;
      const channelCount = Math.max(...buffers.map(buffer => buffer.numberOfChannels));
      const totalLength = buffers.reduce((sum, buffer) => sum + buffer.length, 0);

      const outputBuffer = tempContext.createBuffer(channelCount, totalLength, sampleRate);

      for (let channel = 0; channel < channelCount; channel++) {
        const channelData = outputBuffer.getChannelData(channel);
        let offset = 0;
        buffers.forEach(buffer => {
          const data = buffer.getChannelData(channel < buffer.numberOfChannels ? channel : 0);
          channelData.set(data, offset);
          offset += buffer.length;
        });
      }

      try {
        if (tempContext && tempContext.state !== 'closed') {
          await tempContext.close();
        }
      } catch (closeError) {
        console.warn('关闭临时AudioContext失败', closeError);
      }

      return outputBuffer;
    },

    // 编码为WAV格式
    encodeWav(audioBuffer) {
      if (!audioBuffer) return null;

      const numberOfChannels = audioBuffer.numberOfChannels;
      const sampleRate = audioBuffer.sampleRate;
      const samples = audioBuffer.length;
      const dataLength = samples * numberOfChannels * 2; // 16-bit
      const buffer = new ArrayBuffer(44 + dataLength);
      const view = new DataView(buffer);

      const interleaved = this.interleaveAudioData(audioBuffer, numberOfChannels, samples);
      this.writeWavHeader(view, sampleRate, numberOfChannels, dataLength);

      let offset = 44;
      for (let i = 0; i < interleaved.length; i++, offset += 2) {
        const sample = Math.max(-1, Math.min(1, interleaved[i]));
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
      }

      return new Blob([view], { type: 'audio/wav' });
    },

    // 交错音频数据
    interleaveAudioData(audioBuffer, channelCount, samples) {
      const result = new Float32Array(samples * channelCount);
      const channels = [];
      for (let i = 0; i < channelCount; i++) {
        channels.push(audioBuffer.getChannelData(i < audioBuffer.numberOfChannels ? i : 0));
      }

      let offset = 0;
      for (let i = 0; i < samples; i++) {
        for (let channel = 0; channel < channelCount; channel++) {
          result[offset++] = channels[channel][i] || 0;
        }
      }
      return result;
    },

    // 写入WAV头部
    writeWavHeader(view, sampleRate, channelCount, dataLength) {
      const blockAlign = channelCount * 2;
      const byteRate = sampleRate * blockAlign;

      this.writeString(view, 0, 'RIFF');
      view.setUint32(4, 36 + dataLength, true);
      this.writeString(view, 8, 'WAVE');
      this.writeString(view, 12, 'fmt ');
      view.setUint32(16, 16, true); // Subchunk1Size (PCM)
      view.setUint16(20, 1, true); // AudioFormat (PCM)
      view.setUint16(22, channelCount, true);
      view.setUint32(24, sampleRate, true);
      view.setUint32(28, byteRate, true);
      view.setUint16(32, blockAlign, true);
      view.setUint16(34, 16, true); // BitsPerSample
      this.writeString(view, 36, 'data');
      view.setUint32(40, dataLength, true);
    },

    // 写入字符串
    writeString(view, offset, string) {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    },

    // 触发下载
    triggerDownload(blob, fileName) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }
};
</script>

<style scoped>
.template-container {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 15px;
  background-color: white;
  margin-bottom: 15px;
}

.audio-preview {
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

.station-type-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.station-type-btn {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 120px;
  margin-bottom: 4px;
}

.station-type-btn.active,
.station-type-btn:active {
  background-color: #0d6efd;
  color: #fff;
  border-color: #0d6efd;
}

.sequence-list {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  overflow-y: auto;
  padding: 5px;
  background-color: #f8f9fa;
}

.sequence-item {
  display: flex;
  align-items: center;
  padding: 8px;
  margin-bottom: 2px;
  background-color: white;
  border-radius: 3px;
  cursor: move;
  border: 1px solid transparent;
  transition: all 0.2s ease;
}

.sequence-item:hover {
  background-color: #e9ecef;
  border-color: #6c757d;
}

.sequence-item.active {
  background-color: #cfe2ff;
  border-color: #0d6efd;
}

/* 可选择序列的样式 */
.selectable-sequence {
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.selectable-sequence:hover {
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
}

.selected-sequence {
  border: 2px solid #007bff !important;
  box-shadow: 0 0 10px rgba(0, 123, 255, 0.3);
  background-color: #f8f9ff !important;
}

/* 移除"已选中"文字标签 */

.drag-handle {
  font-weight: bold;
  color: #6c757d;
  margin-right: 8px;
  cursor: grab;
}

.drag-handle:active {
  cursor: grabbing;
}

.sequence-number {
  font-weight: bold;
  margin-right: 8px;
  color: #495057;
  min-width: 30px;
}

.audio-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.9em;
}

/* 拖拽辅助线样式 */
.drop-indicator {
  height: 2px;
  background-color: #000;
  margin: 4px 0;
  border-radius: 1px;
}

.sequence-item.drag-over {
  border-top: 2px solid #000;
  margin-top: 2px;
}

.sequence-item.drag-over .drag-handle {
  color: #000;
}

/* ===== 移动端适配：表单/列表堆叠与可点区域 ===== */
@media (max-width: 768px) {
  .template-container {
    padding: 12px;
  }
  /* 三列布局在小屏改为纵向堆叠 */
  .row > .col-4,
  .row > .col-8 {
    flex: 0 0 100%;
    max-width: 100%;
  }
  .sequence-list {
    max-height: 220px;
  }
  .station-type-btn {
    max-width: 48%;
  }
  .form-label { margin-top: 8px; }
}
</style> 